import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { apiService } from "../../api/services/apiService";
import PmgAssignmentSection from "../product-management-groups/PmgAssignmentSection";
import type { Category, EntityStatus } from "../../types";
import { EntityStatus as EntityStatusEnum, EntityType } from "../../types";

interface EditCategoryDialogProps {
  categoryUuid: string | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: Category) => void;
}

const EditCategoryDialog: React.FC<EditCategoryDialogProps> = ({
  categoryUuid,
  open,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<Category | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pmgAccessDenied, setPmgAccessDenied] = useState(false);
  const readOnly = pmgAccessDenied;

  useEffect(() => {
    if (!open || !categoryUuid) {
      setDraft(null);
      return;
    }
    setLoading(true);
    apiService
      .getCategory(categoryUuid)
      .then(setDraft)
      .catch((err: unknown) => {
        alert(err instanceof Error ? err.message : "Failed to load category");
        onClose();
      })
      .finally(() => setLoading(false));
  }, [open, categoryUuid, onClose]);

  const handleSave = async () => {
    if (!draft?.uuid) return;
    setSaving(true);
    try {
      const updated = await apiService.updateCategory(draft.uuid, {
        name: draft.name,
        status: draft.status,
      });
      onSaved(updated);
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update category");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Edit Category</DialogTitle>
      <DialogContent>
        {loading || !draft ? (
          <CircularProgress />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={draft.name}
              disabled={readOnly}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
            <TextField label="Tenant ID" value={draft.tenantId} disabled />
            <TextField
              select
              label="Status"
              value={draft.status}
              disabled={readOnly}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  status: e.target.value as EntityStatus,
                })
              }
            >
              {Object.values(EntityStatusEnum).map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            {draft.uuid && (
              <PmgAssignmentSection
                entityUuid={draft.uuid}
                entityType={EntityType.CATEGORY}
                onAccessDenied={setPmgAccessDenied}
              />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !draft || pmgAccessDenied}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCategoryDialog;
